import { juzData } from "./juzData";

// --- Build the "Data Center" (Index) ---
// We extract all 114 Surahs and their total ayahs from juzData
const surahsIndex = [];
const surahMap = {};

juzData.forEach((juz) => {
  juz.daftarSurah.forEach((s) => {
    if (!surahMap[s.nomor]) {
      surahMap[s.nomor] = { ...s };
    } else {
      // The total ayahs is the max ayatAkhir found across juz
      surahMap[s.nomor].ayatAkhir = Math.max(surahMap[s.nomor].ayatAkhir, s.ayatAkhir);
    }
  });
});

/**
 * Normalisasi fonetik bahasa Indonesia-Arab untuk mentolerir typo ekstrem
 * (misal: "bakarah" -> "baqarah", "iklas" -> "ikhlas")
 */
function normalizePhonetic(str) {
  return str.toLowerCase()
    .replace(/[^a-z]/g, "") // hanya huruf
    .replace(/(.)\1+/g, "$1") // hapus huruf ganda (contoh: fathhihah -> fathihah)
    .replace(/kh/g, "k")   // ikhlas -> iklas
    .replace(/sh/g, "s")   // ash -> as
    .replace(/sy/g, "s")   // asy -> as
    .replace(/ts/g, "s")   // ats -> as
    .replace(/dz/g, "z")   // dzun -> zun
    .replace(/dh/g, "d")   // dhuha -> duha
    .replace(/th/g, "t")   // fatihah -> fatihah
    .replace(/gh/g, "g")   // maghrib -> magrib
    .replace(/q/g, "k")    // baqarah -> bakarah
    .replace(/f/g, "p")    // fatihah -> patihah
    .replace(/v/g, "p")
    .replace(/z/g, "j");   // zalzalah -> jaljalah
}

function getSkeleton(str) {
  return normalizePhonetic(str).replace(/[aeiou]/g, ""); // hapus huruf vokal
}

Object.values(surahMap)
  .sort((a, b) => a.nomor - b.nomor)
  .forEach((s) => {
    surahsIndex.push({
      nomor: s.nomor,
      nama: s.nama,
      arti: s.arti,
      jumlahAyat: s.ayatAkhir,
      // Create a search key by normalising phonetics
      searchKey: normalizePhonetic(s.nama),
      skeletonKey: getSkeleton(s.nama),
    });
  });

/**
 * Fungsi untuk mencari kemiripan string secara sederhana (Levenstein/Skeleton match)
 */
function fuzzyMatch(query, targetKey, targetSkeleton) {
  const qNormal = normalizePhonetic(query);
  if (!qNormal) return false;
  
  // Exact substring match on normalized string (tolerates 'q' vs 'k', 'f' vs 'p', dll)
  if (targetKey.includes(qNormal)) return true;
  
  // Skeleton match (toleransi ekstrim terhadap hilangnya/salah huruf vokal)
  const qSkeleton = getSkeleton(query);
  if (qSkeleton && targetSkeleton.includes(qSkeleton)) return true;

  return false;
}

const POPULAR_INTENTS = [
  { keywords: ["ayat kursi", "kursi"], surah: 2, ayat: 255, title: "Ayat Kursi", subtitle: "Penghulu Al-Qur'an (Al-Baqarah 255)" },
  { keywords: ["seribu dinar", "1000 dinar", "rezeki"], surah: 65, ayat: 2, title: "Ayat Seribu Dinar", subtitle: "Ayat pembuka rezeki (At-Talaq 2-3)" },
  { keywords: ["sapu jagat", "sapujagat", "doa selamat"], surah: 2, ayat: 201, title: "Doa Sapu Jagat", subtitle: "Keselamatan dunia akhirat (Al-Baqarah 201)" },
  { keywords: ["amanar rasul", "amanarrasul"], surah: 2, ayat: 285, title: "Amanar Rasul", subtitle: "Dua ayat penutup (Al-Baqarah 285-286)" },
  { keywords: ["doa nabi yunus", "dzun nun", "la ilaha", "kesulitan"], surah: 21, ayat: 87, title: "Doa Nabi Yunus", subtitle: "Doa saat kesulitan (Al-Anbiya 87)" },
  { keywords: ["ashabul kahfi", "pemuda gua", "gua"], surah: 18, ayat: 9, title: "Kisah Ashabul Kahfi", subtitle: "Kisah 7 pemuda (Al-Kahfi 9-26)" },
  { keywords: ["luqman", "nasihat luqman"], surah: 31, ayat: 13, title: "Nasihat Luqman", subtitle: "Nasihat kepada anak (Luqman 13)" },
  { keywords: ["doa nabi ibrahim"], surah: 14, ayat: 35, title: "Doa Nabi Ibrahim", subtitle: "Doa negeri aman (Ibrahim 35)" },
  { keywords: ["jumat", "sunnah jumat"], surah: 18, ayat: 1, title: "Surah Al-Kahfi", subtitle: "Sunnah dibaca hari Jumat" },
  { keywords: ["siksa kubur", "penyelamat kubur"], surah: 67, ayat: 1, title: "Surah Al-Mulk", subtitle: "Penyelamat dari siksa kubur" },
  { keywords: ["kaya", "kekayaan", "waqiah", "lancar rezeki"], surah: 56, ayat: 1, title: "Surah Al-Waqi'ah", subtitle: "Surah pelancar rezeki" },
  { keywords: ["jantung quran", "jantung al quran", "yasin", "yaseen"], surah: 36, ayat: 1, title: "Surah Ya-Sin", subtitle: "Jantungnya Al-Qur'an" }
];

/**
 * Smart Search Engine dengan pemahaman User Behavior (NLP Dasar)
 * @param {string} rawQuery
 * @returns {Array} List of suggestions
 */
export function querySmartSearch(rawQuery) {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return [];

  const suggestions = [];
  const addedIds = new Set(); // Prevent duplicates

  const addSuggestion = (sug) => {
    if (!addedIds.has(sug.id)) {
      suggestions.push(sug);
      addedIds.add(sug.id);
    }
  };

  // 0. Cek Populer Intents (Pencarian berbasis niat manusia)
  POPULAR_INTENTS.forEach(intent => {
    const isMatch = intent.keywords.some(kw => query.includes(kw));
    if (isMatch) {
      addSuggestion({
        id: `intent-${intent.surah}-${intent.ayat}`,
        type: "ayat", // kita gunakan icon ayat/surah
        title: intent.title,
        subtitle: intent.subtitle,
        path: `/baca/${intent.surah}${intent.ayat > 1 ? `#verse-${intent.ayat}` : ''}`,
        score: 200, // Top priority
      });
    }
  });

  // 1. Cek pola Juz Eksplisit (Contoh: "juz 30", "juz30", "jz 30")
  const juzMatch = query.match(/^j?u?z?\s*(\d+)$/i);
  if (juzMatch) {
    const juzNum = parseInt(juzMatch[1]);
    if (juzNum >= 1 && juzNum <= 30) {
      addSuggestion({
        id: `juz-${juzNum}`,
        type: "juz",
        title: `Juz ${juzNum}`,
        subtitle: `Menuju awal Juz ${juzNum}`,
        path: `/juz/${juzNum}`,
        score: 150,
      });
    }
  }

  // Membersihkan query dari noise / kata pengisi percakapan
  const cleanQuery = query
    .replace(/\b(surat|surah|ayat|juz|ke|dari|baca|buka|cari|mau|qs\.?|q\.?s\.?|surahnya|ayatnya|dong|tolong)\b/gi, " ")
    .replace(/[\/\\]/g, " ") // handle "2/255"
    .trim()
    .replace(/\s+/g, " ");

  // 2. Cek pola Murni Angka Surah dan Ayat (Contoh: "2 255", "2:255", "qs 2 255")
  const numericAyatMatch = cleanQuery.match(/^(\d+)[\s:\-\.]+(\d+)$/);
  if (numericAyatMatch) {
    const sNum = parseInt(numericAyatMatch[1]);
    const aNum = parseInt(numericAyatMatch[2]);
    if (sNum >= 1 && sNum <= 114) {
      const s = surahsIndex[sNum - 1];
      if (aNum >= 1 && aNum <= s.jumlahAyat) {
        addSuggestion({
          id: `ayat-${s.nomor}-${aNum}`,
          type: "ayat",
          title: `${s.nama} Ayat ${aNum}`,
          subtitle: `Surah ke-${s.nomor} • ${s.arti}`,
          path: `/baca/${s.nomor}#verse-${aNum}`,
          score: 100,
        });
      }
    }
  }

  // Jika user hanya ketik satu angka (misal "30" atau "114")
  const singleNumberMatch = cleanQuery.match(/^(\d+)$/);
  if (singleNumberMatch) {
    const num = parseInt(singleNumberMatch[1]);
    if (num >= 1 && num <= 114) {
      const s = surahsIndex[num - 1];
      addSuggestion({
        id: `surah-${s.nomor}`,
        type: "surah",
        title: `Surah ${s.nama}`,
        subtitle: `Surah ke-${s.nomor} • ${s.arti} • ${s.jumlahAyat} Ayat`,
        path: `/baca/${s.nomor}`,
        score: 90,
      });
    }
  }

  // 3. Ekstrak pola NamaSurah + NomorAyat (Contoh: "al baqarah 55", "baca yasin ayat 9")
  let textPart = cleanQuery;
  let ayatNum = null;
  // Cari pola teks diikuti angka di ujung
  const textAyatMatch = cleanQuery.match(/^([a-z\s\-']+[a-z])\s+(\d+)$/i);
  if (textAyatMatch) {
    textPart = textAyatMatch[1].trim();
    ayatNum = parseInt(textAyatMatch[2]);
  }

  // 4. Lakukan Fuzzy Match pada daftar Surah
  const matchedSurahs = [];
  if (textPart) {
    surahsIndex.forEach((s) => {
      let score = 0;
      const sNameLower = s.nama.toLowerCase();
      
      // Priority 1: Exact match atau awalan
      if (sNameLower === textPart) score = 100;
      else if (sNameLower.startsWith(textPart)) score = 80;
      else if (sNameLower.includes(textPart)) score = 60;
      // Priority 2: Arti match
      else if (s.arti.toLowerCase().includes(textPart)) score = 50;
      // Priority 3: Fuzzy / Skeleton match (menangani typo huruf vokal)
      else if (fuzzyMatch(textPart, s.searchKey, s.skeletonKey)) score = 40;

      if (score > 0) {
        matchedSurahs.push({ surah: s, score });
      }
    });
  }

  // Urutkan berdasarkan score kecocokan
  matchedSurahs.sort((a, b) => b.score - a.score);

  // 5. Build Final Suggestions dari hasil pencarian Surah
  matchedSurahs.forEach((match) => {
    const s = match.surah;
    
    // Jika user mengetik angka ayat di belakang nama surah
    if (ayatNum !== null) {
      if (ayatNum >= 1 && ayatNum <= s.jumlahAyat) {
        addSuggestion({
          id: `ayat-${s.nomor}-${ayatNum}`,
          type: "ayat",
          title: `${s.nama} Ayat ${ayatNum}`,
          subtitle: `Melompat langsung ke ayat ${ayatNum}`,
          path: `/baca/${s.nomor}#verse-${ayatNum}`,
          score: match.score + 10,
        });
      } else {
        addSuggestion({
          id: `surah-${s.nomor}`,
          type: "surah",
          title: `Surah ${s.nama}`,
          subtitle: `Hanya ada ${s.jumlahAyat} ayat di surah ini.`,
          path: `/baca/${s.nomor}`,
          score: match.score,
        });
      }
    } else {
      addSuggestion({
        id: `surah-${s.nomor}`,
        type: "surah",
        title: `Surah ${s.nama}`,
        subtitle: `Surah ke-${s.nomor} • ${s.arti} • ${s.jumlahAyat} Ayat`,
        path: `/baca/${s.nomor}`,
        score: match.score,
      });
    }
  });

  // Sort semua suggestions berdasarkan score dan limit ke 5 hasil terbaik
  return suggestions.sort((a, b) => b.score - a.score).slice(0, 5);
}
